import { type CarDisplayData, WonCarDeliveryStatus } from './types';

/**
 * Self-contained helpers for the admin won-cars feature — ports of the web
 * WonCarsTable display helpers, `cn`, cars/utils `formatCurrency`, and the CHF
 * currency converter (only the surface the won-cars UI consumes).
 */

/** Join truthy class names (stand-in for the web `cn`). */
export function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

/** Format a numeric value as currency, spaces for thousands (web parity). */
export function formatCurrency(
  value: number | string | undefined | null,
  currency = 'USD',
  locale = 'en-US',
): string {
  if (value === undefined || value === null || value === '') return 'N/A';

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return 'N/A';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(numericValue)
    .replace(/,/g, ' ');
}

const FALLBACK_RATES_TO_CHF: Record<string, number> = {
  CHF: 1,
  USD: 0.91,
  EUR: 0.95,
  UAH: 0.022,
  PLN: 0.22,
};

/**
 * Convert an amount from a supported currency into CHF. Tries a free live-rate
 * API, falls back to hardcoded rates (ported from the web currencyConverter).
 */
export async function convertToCHF(
  amount: number,
  fromCurrency: string,
): Promise<{ convertedAmount: number; rate: number }> {
  if (fromCurrency === 'CHF') return { convertedAmount: amount, rate: 1 };

  let rate = FALLBACK_RATES_TO_CHF[fromCurrency] ?? 1;
  try {
    const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${fromCurrency}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (response.ok) {
      const data: { rates?: Record<string, number> } = await response.json();
      if (data.rates && typeof data.rates.CHF === 'number') rate = data.rates.CHF;
    }
  } catch {
    // Keep the fallback rate on any network/parse failure.
  }

  return { convertedAmount: amount * rate, rate };
}

export function getCarBrand(car: CarDisplayData): string {
  return car.ai_brand || car.brand || car.make || '';
}

export function getCarModel(car: CarDisplayData): string {
  return car.ai_model || car.model || '';
}

/** Brand + model, dropping invalid placeholder values (web parity). */
export function formatCarDisplayName(car: CarDisplayData): string {
  const parts = [getCarBrand(car), getCarModel(car)]
    .map((part) => part?.trim())
    .filter((part) => part && part !== '0' && part !== 'null' && part !== 'undefined');
  return parts.join(' ');
}

/** Won date as dd/mm/yyyy, or an em dash when absent. */
export function formatWonDate(date: string | undefined): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** i18n key for a delivery status badge label (web parity). */
export function deliveryStatusTranslationKey(status: WonCarDeliveryStatus): string {
  switch (status) {
    case WonCarDeliveryStatus.UnderAuctionReview:
      return 'wonCars.status.underAuctionReview';
    case WonCarDeliveryStatus.ReleasedToSupplier:
      return 'bids.statusDescriptions.releasedToSupplier';
    case WonCarDeliveryStatus.InTransit:
      return 'bids.statusDescriptions.inTransit';
    case WonCarDeliveryStatus.AtCustoms:
      return 'bids.statusDescriptions.atCustoms';
    case WonCarDeliveryStatus.Delivered:
      return 'bids.statusDescriptions.delivered';
    default:
      return 'wonCars.status.underAuctionReview';
  }
}

/**
 * The artboard's step order. The index in this array IS the step number, and
 * the five-segment rule each row draws is just this array with everything
 * before the current index filled in.
 *
 * Kept identical to `apps/auction`'s `WON_STEPS`: the client watching their car
 * and the operator moving it must be looking at the same five steps, or a
 * support call about "step 3" means two different things.
 */
export const WON_STEPS: readonly WonCarDeliveryStatus[] = [
  WonCarDeliveryStatus.UnderAuctionReview,
  WonCarDeliveryStatus.ReleasedToSupplier,
  WonCarDeliveryStatus.InTransit,
  WonCarDeliveryStatus.AtCustoms,
  WonCarDeliveryStatus.Delivered,
];

/** Step labels under each segment of the rule. */
export const STEP_LABEL_KEYS: readonly string[] = [
  'wonCars.d.steps.payment',
  'wonCars.d.steps.pickupShort',
  'wonCars.d.steps.transport',
  'wonCars.d.steps.customs',
  'wonCars.d.steps.handover',
];

/** A record with no stored status has not moved past the first step. */
export function resolveStatus(status?: WonCarDeliveryStatus): WonCarDeliveryStatus {
  return status && WON_STEPS.includes(status) ? status : WonCarDeliveryStatus.UnderAuctionReview;
}

/** Index of a record's status in `WON_STEPS` — never negative. */
export function stepIndexFor(status?: WonCarDeliveryStatus): number {
  const index = WON_STEPS.indexOf(resolveStatus(status));
  return index < 0 ? 0 : index;
}

export interface StatusVisual {
  /** Short chip wording the artboard prints, not the long status name. */
  chipKey: string;
  /** Chip ground + foreground, as one class string. */
  chip: string;
  /** Filled segment of the progress rule. */
  bar: string;
  /** The row's 3px inset left rule, as a complete arbitrary-value class. */
  stripe: string;
}

/**
 * Status → the colours Design D gives it, on the `cf-` ramp.
 *
 * Amber means "waiting on money", blue means "in motion", green means "done" —
 * the same three meanings, and the same three tokens, as the client-facing
 * won-cars screen in `apps/auction`. The three logistics states deliberately
 * share one pair: to an operator scanning the list they are one condition, and
 * three blues would imply a distinction the workflow does not have.
 */
export const STATUS_VISUAL: Record<WonCarDeliveryStatus, StatusVisual> = {
  [WonCarDeliveryStatus.UnderAuctionReview]: {
    chipKey: 'wonCars.d.chip.awaitingPayment',
    chip: 'bg-cf-warn-tint text-cf-warn',
    bar: 'bg-cf-accent',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-accent)]',
  },
  [WonCarDeliveryStatus.ReleasedToSupplier]: {
    chipKey: 'wonCars.d.chip.released',
    chip: 'bg-cf-info-tint text-cf-info-ink',
    bar: 'bg-cf-info',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-info)]',
  },
  [WonCarDeliveryStatus.InTransit]: {
    chipKey: 'wonCars.d.chip.inTransit',
    chip: 'bg-cf-info-tint text-cf-info-ink',
    bar: 'bg-cf-info',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-info)]',
  },
  [WonCarDeliveryStatus.AtCustoms]: {
    chipKey: 'wonCars.d.chip.atCustoms',
    chip: 'bg-cf-info-tint text-cf-info-ink',
    bar: 'bg-cf-info',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-info)]',
  },
  [WonCarDeliveryStatus.Delivered]: {
    chipKey: 'wonCars.d.chip.received',
    chip: 'bg-cf-success-tint text-cf-success',
    bar: 'bg-cf-success',
    stripe: 'shadow-[inset_3px_0_0_var(--color-cf-success)]',
  },
};

/**
 * Chip classes for a delivery status, on the Design D ramp.
 *
 * These were raw Tailwind (`bg-purple-100`) in the ported web table. Purple was
 * never a colour of this design system; the five states now read from the same
 * tokens the client-facing screen and the progress rule use.
 */
export function deliveryStatusColorClasses(status: WonCarDeliveryStatus): string {
  return STATUS_VISUAL[status]?.chip ?? 'bg-cf-fill text-cf-ink-subtle';
}

/** Short chip wording for a status («Очікує оплати»), not the long name. */
export function deliveryStatusChipKey(status: WonCarDeliveryStatus): string {
  return STATUS_VISUAL[status]?.chipKey ?? 'wonCars.d.chip.awaitingPayment';
}

/** The four status tabs over the list. */
export type WonTabKey = 'all' | 'pay' | 'transit' | 'done';

export const WON_TABS: readonly WonTabKey[] = ['all', 'pay', 'transit', 'done'];

/**
 * Which tab a record belongs to. «В дорозі» deliberately covers released,
 * in-transit AND at-customs: from the operator's side customs is part of the
 * journey, not a destination, and the artboard gives them one tab and one count.
 */
export function tabForStatus(status?: WonCarDeliveryStatus): Exclude<WonTabKey, 'all'> {
  switch (resolveStatus(status)) {
    case WonCarDeliveryStatus.UnderAuctionReview:
      return 'pay';
    case WonCarDeliveryStatus.Delivered:
      return 'done';
    default:
      return 'transit';
  }
}

/** The financial columns that add onto the winning bid to make the deal total. */
const COST_FIELDS = [
  'auctionCommission',
  'delivery',
  'certificate',
  'terminal',
  'customsClearance',
  'repair',
] as const;

/**
 * Deal total in CHF — the winning bid plus every recorded cost.
 *
 * The artboard prints `CHF {bid} · €{landed}`, a landed cost in euros. api-node
 * stores every one of these fields in CHF and serves no FX rate with the
 * record, so this returns CHF and the row labels it CHF. Printing a € sign over
 * a franc figure would be a made-up number on a screen used to settle deals.
 */
export function dealTotalChf(wonCar: {
  bid?: { bid: number } | null;
  auctionCommission?: number;
  delivery?: number;
  certificate?: number;
  terminal?: number;
  customsClearance?: number;
  repair?: number;
}): number {
  return COST_FIELDS.reduce((sum, field) => sum + (wonCar[field] ?? 0), wonCar.bid?.bid ?? 0);
}

/** Amount without the currency word — the artboard prints `CHF` separately. */
export function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(Number(value))
    .replace(/,/g, ' ');
}
